// Formatting while you type, like Discord's composer: **bold** looks bold, ||spoilers|| get their bar, marks fade.
//
// The text box stays a real <textarea> (typing, the caret, autocomplete, IME, phone keyboards and undo all work as
// before) with its text made transparent; this layer draws the same text, styled, exactly underneath. That only lines
// up if every styled character is as wide as the plain one, so nothing here changes widths: bold is a thin outline,
// code is a background rather than a monospace font, and headings are bold rather than bigger. The layer draws in the
// textarea's own font (the device's, #144). Italic slants only where that font's italic face keeps the upright's
// widths (measured once per font, `italicKeepsWidths`); elsewhere emphasis is a dotted underline.
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { parseSource, type SrcNode } from "@bloom/shared";

/** Textarea styles the layer copies, so its text wraps on exactly the same lines. */
const COPY = [
  "boxSizing", "fontSize", "fontWeight", "fontStyle", "fontStretch", "fontVariant", "fontFeatureSettings", "fontVariationSettings", "fontKerning",
  "letterSpacing", "wordSpacing", "lineHeight", "textTransform", "textIndent", "textAlign", "tabSize", "direction", "whiteSpace", "overflowWrap", "wordBreak",
  "paddingTop", "paddingBottom", "paddingLeft", "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth",
] as const;

export interface Highlights {
  /** Members' names: a typed "@Name" gets the mention colour. */
  people?: string[];
  /** Channel names: a typed "#name" gets the channel colour. */
  channels?: string[];
  /** The server's own emoji: a typed ":name:" draws as its picture (FR: emoji from the menu didn't show in the box). */
  emoji?: { name: string; src: string }[];
}

const italicWidths = new Map<string, boolean>();

/** Whether `font`'s italic is exactly as wide as its upright, so slanting emphasis can't move the caret. */
function italicKeepsWidths(cs: CSSStyleDeclaration): boolean {
  const key = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  let same = italicWidths.get(key);
  if (same === undefined) {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return false;
    const sample = "The quick brown fox jumps over the lazy dog, 0123456789 (ÅÉÎõü) — fiflmm WWii";
    ctx.font = key;
    const upright = ctx.measureText(sample).width;
    ctx.font = `italic ${key}`;
    same = Math.abs(ctx.measureText(sample).width - upright) < 0.5;
    italicWidths.set(key, same);
  }
  return same;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function chipsRe({ people = [], channels = [] }: Highlights): RegExp | null {
  const names = [...people, "everyone", "Bloom"].filter(Boolean).map(esc).sort((a, b) => b.length - a.length);
  const chans = channels.filter(Boolean).map(esc).sort((a, b) => b.length - a.length);
  const alts = [`@(?:${names.join("|")})`, ...(chans.length ? [`#(?:${chans.join("|")})`] : [])];
  return new RegExp(`(^|[\\s(\\[{"'])(${alts.join("|")})(?![\\p{L}\\p{N}_-])`, "giu");
}

/** ":name:" for the server's emoji, longest names first; null when there are none. */
function emojiRe(emoji: Highlights["emoji"]): RegExp | null {
  const names = (emoji ?? []).map((e) => esc(e.name)).sort((a, b) => b.length - a.length);
  return names.length ? new RegExp(`:(${names.join("|")}):`, "g") : null;
}

/**
 * A server emoji keeps its ":name:" in the box (it's sent as its token) and the caret must stay where the characters
 * are, so the layer keeps the text, makes it invisible and draws the picture in that same space.
 */
function emojis(text: string, re: RegExp | null, srcs: Map<string, string>, chipRe: RegExp | null, key: string): ReactNode {
  if (!re || !text.includes(":")) return chips(text, chipRe, key);
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const src = srcs.get(m[1]!);
    if (!src) continue;
    if (m.index! > last) out.push(<Fragment key={`${key}t${last}`}>{chips(text.slice(last, m.index!), chipRe, `${key}t${last}`)}</Fragment>);
    out.push(<span key={`${key}e${m.index}`} className="fl-cemoji" style={{ backgroundImage: `url("${src}")` }}>{m[0]}</span>);
    last = m.index! + m[0].length;
  }
  if (!out.length) return chips(text, chipRe, key);
  if (last < text.length) out.push(<Fragment key={`${key}t${last}`}>{chips(text.slice(last), chipRe, `${key}t${last}`)}</Fragment>);
  return <>{out}</>;
}

function chips(text: string, re: RegExp | null, key: string): ReactNode {
  if (!re || !/[@#]/.test(text)) return text;
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index! + m[1]!.length;
    if (start > last) out.push(text.slice(last, start));
    out.push(<span key={`${key}c${start}`} className="fl-chip">{m[2]}</span>);
    last = start + m[2]!.length;
  }
  if (!out.length) return text;
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

interface Res {
  chips: RegExp | null;
  emoji: RegExp | null;
  srcs: Map<string, string>;
}

function draw(nodes: SrcNode[], re: Res, key: string, inCode = false): ReactNode[] {
  return nodes.map((n, i) => {
    const k = `${key}.${i}`;
    // Inside code nothing is a mention or an emoji (as in the sent message).
    if (n.t === "text") return <Fragment key={k}>{inCode ? n.v : emojis(n.v, re.emoji, re.srcs, re.chips, k)}</Fragment>;
    if (n.t === "mark") return <span key={k} className="fl-mark">{n.v}</span>;
    const code = inCode || n.t === "code" || n.t === "codeblock";
    const cls = n.t === "heading" ? `fl-heading fl-h${n.level}` : `fl-${n.t}`;
    return <span key={k} className={cls}>{draw(n.c, re, k, code)}</span>;
  });
}

/**
 * Put this right before the textarea, inside an element with class "fl-field" (position: relative) that wraps both.
 * `text` is the textarea's value.
 */
export function FormatLayer({ area, text, highlights }: { area: RefObject<HTMLTextAreaElement | null>; text: string; highlights?: Highlights }) {
  const layer = useRef<HTMLDivElement>(null);

  const attached = useRef<{ ta: HTMLTextAreaElement; off: () => void } | null>(null);

  // Every render: same box as the textarea (its text styles, room for its scrollbar, its scroll position). The
  // textarea can appear or be replaced after this mounts, so listeners follow whichever one is current.
  useLayoutEffect(() => {
    const ta = area.current;
    const el = layer.current;
    if (!el) return;
    if (attached.current && attached.current.ta !== ta) {
      attached.current.off();
      attached.current = null;
    }
    if (!ta) return;
    const sync = () => {
      const cs = getComputedStyle(ta);
      for (const p of COPY) el.style[p] = cs[p];
      el.style.fontFamily = cs.fontFamily;
      el.dataset.em = italicKeepsWidths(cs) ? "slant" : "flat";
      const scrollbar = ta.offsetWidth - ta.clientWidth - parseFloat(cs.borderLeftWidth) - parseFloat(cs.borderRightWidth);
      el.style.paddingRight = `${parseFloat(cs.paddingRight) + Math.max(0, scrollbar)}px`;
      el.scrollTop = ta.scrollTop;
    };
    sync();
    if (!attached.current) {
      const onScroll = () => (el.scrollTop = ta.scrollTop);
      ta.addEventListener("scroll", onScroll, { passive: true });
      const ro = new ResizeObserver(sync);
      ro.observe(ta);
      attached.current = {
        ta,
        off: () => {
          ta.removeEventListener("scroll", onScroll);
          ro.disconnect();
        },
      };
    }
  });
  // A layer placed before its textarea runs its layout effect before React attaches the textarea's ref, so the first
  // pass finds no textarea (the edit box opened unstyled: its caret off the drawn text until the first keystroke).
  // By the passive effect every ref is attached: catch up then.
  const [, catchUp] = useState(0);
  useEffect(() => {
    if (area.current && attached.current?.ta !== area.current) catchUp((n) => n + 1);
  });
  useLayoutEffect(() => () => attached.current?.off(), []);

  const res: Res = {
    chips: highlights && (highlights.people || highlights.channels) ? chipsRe(highlights) : null,
    emoji: emojiRe(highlights?.emoji),
    srcs: new Map((highlights?.emoji ?? []).map((e) => [e.name, e.src])),
  };
  return (
    <div ref={layer} className="fl-layer" aria-hidden="true">
      {draw(parseSource(text), res, "f")}
      {/* A final line break only takes up a line in a div when something follows it. */}
      {text.endsWith("\n") ? "​" : null}
    </div>
  );
}
